import { useEffect, useState } from "react";
import { FaStar, FaRegStar } from "react-icons/fa";
import api from "../../../configs/api";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { ModalWrapper } from "../common/ModalWrapper";

/* Interactive Star Picker */
const StarPicker = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => {
        const starVal = i + 1;
        const filled = starVal <= (hovered || value);
        return (
          <button
            key={i}
            type="button"
            onClick={() => onChange(starVal)}
            onMouseEnter={() => setHovered(starVal)}
            onMouseLeave={() => setHovered(0)}
            className="transition-transform hover:scale-110"
          >
            {filled ? (
              <FaStar size={22} className="text-yellow-400" />
            ) : (
              <FaRegStar size={22} className="text-yellow-300/50" />
            )}
          </button>
        );
      })}
      {value > 0 && (
        <span className="ml-2 text-xs text-descriptionText">
          {value === 1 && "Poor"}
          {value === 2 && "Fair"}
          {value === 3 && "Good"}
          {value === 4 && "Very Good"}
          {value === 5 && "Excellent"}
        </span>
      )}
    </div>
  );
};

const EditReviewModal = ({ modal, onReviewUpdated }) => {
  // ✅ EXACT same pattern as EditProjectModal
  const { isOpen, mode, data: reviewId, close } = modal;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: "",
    description: "",
    rating: 0,
    status: "active",
  });

  useEffect(() => {
    if (!isOpen || mode !== "edit" || !reviewId) return;

    const fetchReview = async () => {
      try {
        setLoading(true);
        setErrors({});
        const res = await api.get(`/reviews/${reviewId}`);
        const review = res.data?.data ?? res.data;
        setForm({
          name: review.name ?? "",
          description: review.description ?? "",
          rating: review.rating ?? 0,
          status: review.status ?? "active",
        });
      } catch (error) {
        console.error(error);
        showError("Failed to load review");
        close();
      } finally {
        setLoading(false);
      }
    };

    fetchReview();
  }, [isOpen, mode, reviewId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setForm({ name: "", description: "", rating: 0, status: "active" });
      setErrors({});
    }
  }, [isOpen]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.description.trim()) e.description = "Description is required";
    if (!form.rating || form.rating === 0) e.rating = "Please select a rating";
    return e;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    try {
      setSaving(true);
      const res = await api.put(`/reviews/${reviewId}`, form);
      const updated = res.data?.data ?? res.data;
      onReviewUpdated?.(updated);
      showSuccess("Review updated successfully");
      close();
    } catch (error) {
      console.error(error);
      showError(error?.response?.data?.message || "Failed to update review");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper
      title="Edit Review"
      isOpen={isOpen && mode === "edit"}
      onClose={close}
      primaryActionLabel="Save Changes"
      primaryLoading={saving}
      allowEasyClose={false}
      onPrimaryAction={handleSubmit}
      maxWidth="max-w-lg"
    >
      {loading ? (
        <div className="px-1 py-4 space-y-5 animate-pulse">
          <div className="space-y-1.5">
            <div className="h-3 bg-divider rounded w-16" />
            <div className="flex gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="w-6 h-6 bg-divider rounded" />
              ))}
            </div>
          </div>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-3 bg-divider rounded w-24" />
              <div className={`bg-divider rounded w-full ${i === 1 ? "h-24" : "h-10"}`} />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          {/* NAME */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Reviewer name"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className={`mt-1 h-[44px] w-full px-4 border text-sm outline-none focus:border-primaryAccent transition-colors ${
                errors.name ? "border-red-400" : "border-borderLight"
              }`}
            />
            {errors.name && (
              <p className="text-xs text-red-500 mt-1">{errors.name}</p>
            )}
          </div>

          {/* RATING */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Rating <span className="text-red-500">*</span>
            </label>
            <div className="mt-2">
              <StarPicker
                value={form.rating}
                onChange={(val) => handleChange("rating", val)}
              />
            </div>
            {errors.rating && (
              <p className="text-xs text-red-500 mt-1">{errors.rating}</p>
            )}
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={4}
              placeholder="Write the review..."
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              className={`mt-1 w-full px-4 py-3 border text-sm outline-none focus:border-primaryAccent transition-colors resize-none ${
                errors.description ? "border-red-400" : "border-borderLight"
              }`}
            />
            {errors.description && (
              <p className="text-xs text-red-500 mt-1">{errors.description}</p>
            )}
          </div>

          {/* STATUS */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Status
            </label>
            <select
              value={form.status}
              onChange={(e) => handleChange("status", e.target.value)}
              className="mt-1 w-full h-[44px] px-4 border border-borderLight text-sm outline-none focus:border-primaryAccent transition-colors"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      )}
    </ModalWrapper>
  );
};

export default EditReviewModal;